import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowLeft, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Forgot Password page — Sprint 2 Batch 2 (May 2026).
// Step 1 of 2: user enters their email; backend emails a 6-digit code.
// Always shows a success message, regardless of whether the email exists
// (anti-enumeration). On submit redirects to /reset-password with email
// prefilled.
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      // Backend always returns 200 here, so anything reaching this branch is
      // a network error. Tell the user.
      setError("Could not reach the server. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/login" data-testid="back-to-login-link" className="inline-flex items-center gap-2 text-sm text-[#2563EB] hover:text-blue-700 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-password-sent">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center">
                <Mail className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
                Check your inbox
              </h1>
              <p className="text-sm text-slate-600 leading-relaxed">
                If an account exists with <span className="font-semibold">{email}</span>, we've sent
                a 6-digit code. It expires in 15 minutes.
              </p>
              <button
                onClick={() => nav(`/reset-password?email=${encodeURIComponent(email)}`)}
                data-testid="enter-code-button"
                className="w-full bg-[#2563EB] hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-colors"
              >
                Enter the code
              </button>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                data-testid="resend-button"
                className="text-xs text-slate-500 hover:text-slate-700 underline"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#2563EB]" />
                </div>
                <div>
                  <h1 className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Forgot your password?</h1>
                  <p className="text-xs text-slate-500 mt-0.5">We'll email you a 6-digit reset code.</p>
                </div>
              </div>
              <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Email address</label>
                  <input
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourcenter.com"
                    data-testid="email-input"
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  />
                </div>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg" data-testid="forgot-password-error">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || !email}
                  data-testid="forgot-password-submit"
                  className="w-full bg-[#2563EB] hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Email me a reset code"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
